import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import agentApiService from '../../../services/agentApiService';

const ProfileCompletionModal = ({ isOpen, onClose, onProfileCompleted }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    gstNumber: '',
    udyamAadharRegNo: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchProfileData();
    }
  }, [isOpen]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await agentApiService.getAgentProfileForCompletion();
      
      if (response.success) {
        setProfileData(response.profile);
        // Pre-fill form with existing data
        setFormData({
          gstNumber: response.profile.gstNumber || '',
          udyamAadharRegNo: response.profile.udyamAadharRegNo || ''
        });
        console.log('✅ Profile data loaded:', response.profile);
      } else {
        toast.error(response.message || 'Failed to load profile data');
      }
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.toUpperCase() // Convert to uppercase for GST/Udyam
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // GST Number validation
    if (!formData.gstNumber || formData.gstNumber.trim() === '') {
      newErrors.gstNumber = 'GST Number is required';
    } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}Z[0-9]{1}$/.test(formData.gstNumber)) {
      newErrors.gstNumber = 'Invalid GST format (e.g., 22AAAAA0000A1Z5)';
    }
    
    // Udyam Aadhar validation
    if (!formData.udyamAadharRegNo || formData.udyamAadharRegNo.trim() === '') {
      newErrors.udyamAadharRegNo = 'Udyam Aadhar Number is required';
    } else if (!/^\d{12}$/.test(formData.udyamAadharRegNo)) {
      newErrors.udyamAadharRegNo = 'Must be a 12-digit number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await agentApiService.updateAgentProfile(
        profileData.agentId,
        formData
      );
      
      if (response.success) {
        toast.success('Profile updated successfully!');
        console.log('✅ Profile updated successfully');
        
        // Close modal and notify parent
        onClose();
        if (onProfileCompleted) {
          onProfileCompleted();
        }
      } else {
        toast.error(response.message || 'Failed to update profile');
        if (response.errors) {
          setErrors(response.errors);
        }
      }
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="w-10 h-10 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-bold text-gray-900">Complete Your Profile</h4>
                    <p className="text-sm text-gray-600">
                      Please provide the following details to proceed with booking
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Current Profile Info */}
                {profileData && (
                  <div className="mb-6 bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Your Current Information</h5>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">{profileData.firstName} {profileData.lastName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <span className="ml-2 font-medium">{profileData.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Phone:</span>
                        <span className="ml-2 font-medium">{profileData.phoneNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Business:</span>
                        <span className="ml-2 font-medium">{profileData.businessName || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                <div className="space-y-4">
                  {/* GST Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={formData.gstNumber}
                      onChange={handleInputChange}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        errors.gstNumber 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />
                    {errors.gstNumber && (
                      <p className="mt-1 text-sm text-red-600">{errors.gstNumber}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Format: 2 digits + 5 letters + 4 digits + 1 letter + 1 digit + Z + 1 digit
                    </p>
                  </div>

                  {/* Udyam Aadhar Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Udyam Aadhar Registration Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="udyamAadharRegNo"
                      value={formData.udyamAadharRegNo}
                      onChange={handleInputChange}
                      placeholder="123456789012"
                      maxLength={12}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        errors.udyamAadharRegNo 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />
                    {errors.udyamAadharRegNo && (
                      <p className="mt-1 text-sm text-red-600">{errors.udyamAadharRegNo}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Must be a 12-digit number
                    </p>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h5 className="text-sm font-medium text-blue-900">Why do we need this?</h5>
                      <p className="mt-1 text-sm text-blue-800">
                        GST and Udyam Aadhar details are required for business verification and compliance purposes.
                        Your information will be sent for admin approval.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex space-x-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit & Continue'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
